import './DeliverySnapshot.scss';
import cn from 'classnames';
import {
  CrossChannelMetrics,
  BudgetsData,
  Metric
} from '@blisspointmedia/bpm-types/dist/CrossChannel';
import { useState, useMemo, useContext, useCallback } from 'react';
import * as R from 'ramda';
import {
  getMetricFormatter,
  MetricFormatter
} from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Placement, Size } from '@Components/designConstants';
import { ChartContainer } from '@Components/ChartContainer';
import { downloadPNG, exportMultiSheetToExcel } from '@src/utils/download-utils';
import { DateRange } from '@src/utils/date-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { Brand100, Neutral500 } from '@src/utils/colors';
import { CrossChannelContext } from '../cross-channel-context';
import { PacingSnapshotChart, BarPart, BarType, DataItem } from './PacingSnapshotChart';

const TopDropdownValues = {
  SPEND: 'spend',
  OTHER_BUDGET: 'otherBudget'
} as const;
type TopDropdownValuesType = (typeof TopDropdownValues)[keyof typeof TopDropdownValues];

const BottomDropdownValues = {
  BUDGET: 'budget',
  OTHER_SPEND: 'otherSpend'
} as const;
type BottomDropdownValuesType = (typeof BottomDropdownValues)[keyof typeof BottomDropdownValues];

interface DeliverySnapshotProps {
  data: CrossChannelMetrics[];
  otherData: CrossChannelMetrics[];
  budgetsData: BudgetsData[];
  defaultTop?: string;
  defaultBottom?: string;
}

const isTopDropdownValue = (value?: string): value is TopDropdownValuesType =>
  value === TopDropdownValues.SPEND || value === TopDropdownValues.OTHER_BUDGET;

const isBottomDropdownValue = (value?: string): value is BottomDropdownValuesType =>
  value === BottomDropdownValues.BUDGET || value === BottomDropdownValues.OTHER_SPEND;

interface ChannelTotals {
  primarySpend: Record<string, number>;
  comparisonSpend: Record<string, number>;
  primaryBudgets: Record<string, number>;
  comparisonBudgets: Record<string, number>;
}

const getChannelTotals = (
  data: CrossChannelMetrics[],
  otherData: CrossChannelMetrics[],
  budgetsData: BudgetsData[],
  toggledChannels: Record<string, boolean>
): ChannelTotals => {
  let primarySpendByChannel: Record<string, number> = {};
  let comparisonSpendByChannel: Record<string, number> = {};
  let primaryBudgetByChannel: Record<string, number> = {};
  let comparisonBudgetByChannel: Record<string, number> = {};

  for (const item of data) {
    const { channel, metrics } = item;
    if (!toggledChannels[channel]) {
      continue;
    }

    const { spend } = metrics;

    primarySpendByChannel = R.mergeDeepRight(primarySpendByChannel, {
      [channel]: R.pathOr(0, [channel], primarySpendByChannel) + spend
    });
  }

  for (const item of otherData) {
    const { channel, metrics } = item;
    if (!toggledChannels[channel]) {
      continue;
    }

    const { spend } = metrics;

    comparisonSpendByChannel = R.mergeDeepRight(comparisonSpendByChannel, {
      [channel]: R.pathOr(0, [channel], comparisonSpendByChannel) + spend
    });
  }

  for (const item of budgetsData) {
    const { channel, budget, dateRange } = item;
    if (!toggledChannels[channel]) {
      continue;
    }

    if (dateRange === 'primary') {
      primaryBudgetByChannel = R.mergeDeepRight(primaryBudgetByChannel, {
        [channel]: R.pathOr(0, [channel], primaryBudgetByChannel) + budget
      });
    } else {
      comparisonBudgetByChannel = R.mergeDeepRight(comparisonBudgetByChannel, {
        [channel]: R.pathOr(0, [channel], comparisonBudgetByChannel) + budget
      });
    }
  }

  return {
    primarySpend: primarySpendByChannel,
    comparisonSpend: comparisonSpendByChannel,
    primaryBudgets: primaryBudgetByChannel,
    comparisonBudgets: comparisonBudgetByChannel
  };
};

/**
 * Choose which numbers to use for each channel depending on what comparison dropdowns are selected.
 */
const processSnapshotData = (
  channelTotals: ChannelTotals,
  toggledChannels: Record<string, boolean>,
  topPathKey: TopDropdownValuesType,
  bottomPathKey: BottomDropdownValuesType,
  isDoubleBar: boolean,
  channelColors: Record<string, string>,
  availableChannels: Record<string, boolean>,
  metricFormatter: MetricFormatter
): DataItem[] => {
  const { primarySpend, comparisonSpend, primaryBudgets, comparisonBudgets } = channelTotals;

  const processedData: DataItem[] = [];

  if (isDoubleBar) {
    for (const channel of R.keys(availableChannels).sort()) {
      if (!toggledChannels[channel]) {
        continue;
      }

      const topValue =
        topPathKey === TopDropdownValues.SPEND
          ? primarySpend[channel] || 0
          : comparisonBudgets[channel] || 0;

      const bottomValue =
        bottomPathKey === BottomDropdownValues.OTHER_SPEND
          ? comparisonSpend[channel] || 0
          : primaryBudgets[channel] || 0;

      const parts = [
        { val: topValue, type: BarType.HORIZONTAL },
        { val: bottomValue, type: BarType.HORIZONTAL_GREY }
      ];

      const numericalDifference = topValue - bottomValue;
      const percentDifference = (numericalDifference / bottomValue) * 100;

      const formattedNumericalDifference = metricFormatter(Metric.SPEND)(numericalDifference);
      const formattedPercentDifference = Number.isFinite(percentDifference)
        ? `(${Math.round(percentDifference)}%)`
        : '';

      const subLabel = `${formattedNumericalDifference} ${formattedPercentDifference}`;

      processedData.push({
        label: channel,
        subLabel,
        topValue,
        bottomValue,
        parts,
        color: channelColors[channel]
      });
    }
  } else {
    for (const channel of R.keys(availableChannels).sort()) {
      if (!toggledChannels[channel]) {
        continue;
      }

      // always spend
      const topValue =
        topPathKey === TopDropdownValues.SPEND
          ? primarySpend[channel] || 0
          : comparisonSpend[channel] || 0;

      // always budget
      const bottomValue =
        bottomPathKey === BottomDropdownValues.BUDGET
          ? primaryBudgets[channel] || 0
          : comparisonBudgets[channel] || 0;

      const parts: BarPart[] = [
        { val: topValue, type: BarType.HORIZONTAL, className: 'spend' },
        { val: bottomValue, type: BarType.VERTICAL, className: 'budget' }
      ];

      const difference = bottomValue - topValue;

      if (difference > 0) {
        parts.push({ val: difference, type: BarType.HORIZONTAL_GREY, className: 'remainder' });
      }

      const numericalDifference = topValue - bottomValue;
      const percentDifference = (numericalDifference / bottomValue) * 100;

      const formattedNumericalDifference = metricFormatter(Metric.SPEND)(numericalDifference);
      const formattedPercentDifference = Number.isFinite(percentDifference)
        ? `(${Math.round(percentDifference)}%)`
        : '';

      const subLabel = `${formattedNumericalDifference} ${formattedPercentDifference}`;

      processedData.push({
        label: channel,
        subLabel,
        topValue,
        bottomValue,
        parts,
        color: channelColors[channel]
      });
    }
  }

  return processedData.sort(
    (a, b) => Math.max(b.topValue, b.bottomValue || 0) - Math.max(a.topValue, a.bottomValue || 0)
  );
};

const getTotals = (processedData: DataItem[]) => {
  const topTotal = processedData.reduce((acc, item) => acc + R.pathOr(0, ['topValue'], item), 0);
  const bottomTotal = processedData.reduce(
    (acc, item) => acc + R.pathOr(0, ['bottomValue'], item),
    0
  );

  return { topTotal, bottomTotal };
};

export const makeDropdownOptions = () => {
  const topDropdownOptions = [
    { label: "Comparison's Budget", value: TopDropdownValues.OTHER_BUDGET },
    { label: "Primary's Spend", value: TopDropdownValues.SPEND }
  ];
  const bottomDropdownOptions = [
    { label: "Primary's Budget", value: BottomDropdownValues.BUDGET },
    { label: "Comparison's Spend", value: BottomDropdownValues.OTHER_SPEND }
  ];

  return { topDropdownOptions, bottomDropdownOptions };
};

export const DeliverySnapshot = ({
  data,
  otherData,
  budgetsData,
  defaultTop,
  defaultBottom
}: DeliverySnapshotProps) => {
  const [topPathKey, setTopPathKey] = useState<TopDropdownValuesType>(
    isTopDropdownValue(defaultTop) ? defaultTop : TopDropdownValues.SPEND
  );
  const [bottomPathKey, setBottomPathKey] = useState<BottomDropdownValuesType>(
    isBottomDropdownValue(defaultBottom) ? defaultBottom : BottomDropdownValues.BUDGET
  );

  const {
    dates,
    selectedChannels,
    deliveryAndPerformanceChannels,
    channelColors,
    kpi,
    otherDates,
    dataInfo
  } = useContext(CrossChannelContext);
  const { currency_code: currencyCode } = useCompanyInfo();
  const metricFormatter = useMemo(() => getMetricFormatter(currencyCode), [currencyCode]);

  const channelTotals = useMemo(
    () => getChannelTotals(data, otherData, budgetsData, selectedChannels),
    [data, otherData, budgetsData, selectedChannels]
  );

  // If comparing spend/spend or budget/budget, we display the data differently.
  const isDoubleBar =
    (topPathKey === TopDropdownValues.SPEND &&
      bottomPathKey === BottomDropdownValues.OTHER_SPEND) ||
    (topPathKey === TopDropdownValues.OTHER_BUDGET &&
      bottomPathKey === BottomDropdownValues.BUDGET);

  const processedData = useMemo(
    () =>
      processSnapshotData(
        channelTotals,
        selectedChannels,
        topPathKey,
        bottomPathKey,
        isDoubleBar,
        channelColors,
        deliveryAndPerformanceChannels,
        metricFormatter
      ),
    [
      channelTotals,
      selectedChannels,
      topPathKey,
      bottomPathKey,
      isDoubleBar,
      channelColors,
      deliveryAndPerformanceChannels,
      metricFormatter
    ]
  );

  const { topDropdownOptions, bottomDropdownOptions } = useMemo(() => makeDropdownOptions(), []);

  const fileNameIdentifiers = useMemo(() => {
    const topDropdown =
      topDropdownOptions.find(item => item.value === topPathKey)?.label.split(' ')[1] || topPathKey;
    const bottomDropdown =
      bottomDropdownOptions.find(item => item.value === bottomPathKey)?.label.split(' ')[1] ||
      bottomPathKey;

    const fileNameItems = ['CrossChannel', `${dates.start}–${dates.end}`, topDropdown];
    if (isDoubleBar) {
      fileNameItems.push(`vs_${otherDates.start}–${otherDates.end}`, bottomDropdown);
    }
    return fileNameItems;
  }, [
    topDropdownOptions,
    topPathKey,
    bottomDropdownOptions,
    bottomPathKey,
    otherDates,
    dates,
    isDoubleBar
  ]);

  const excelDownload = useCallback(() => {
    const exportData = processExport(data, otherData, budgetsData, dates, otherDates);

    exportMultiSheetToExcel(
      [
        { name: 'Delivery Snapshot', data: exportData },
        { name: 'Data Info', data: dataInfo }
      ],
      fileNameIdentifiers.join('_')
    );
  }, [data, otherData, budgetsData, dates, otherDates, fileNameIdentifiers, dataInfo]);

  const pngDownload = useCallback(async () => {
    const label = `KPI: ${kpi}`;
    await downloadPNG('.delivery-snapshot-container', fileNameIdentifiers, label, ['.right-side']);
  }, [kpi, fileNameIdentifiers]);

  const { topTotal, bottomTotal } = useMemo(() => getTotals(processedData), [processedData]);

  // When Spend is on top and Budget is on bottom, the budget dropdown uses the darker
  // budget-bar color; every other combination keeps the top dropdown as the darker color.
  const flipDropdownColors = useMemo(
    () => topPathKey === TopDropdownValues.SPEND && bottomPathKey === BottomDropdownValues.BUDGET,
    [topPathKey, bottomPathKey]
  );

  return (
    <div className='delivery-snapshot-container'>
      <ChartContainer
        title={
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            className={cn('top-delivery-snapshot-dropdown', { 'flip-color': flipDropdownColors })}
            type={DropdownToggleType.WIDGET_TITLE}
            value={topPathKey}
            options={topDropdownOptions}
            onChange={option => setTopPathKey(option)}
          />
        }
        leftActions={
          <>
            <div className='vs-label' style={{ color: flipDropdownColors ? Brand100 : Neutral500 }}>
              vs
            </div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              size={Size.LG}
              className={cn('bottom-delivery-snapshot-dropdown', {
                'flip-color': flipDropdownColors
              })}
              type={DropdownToggleType.WIDGET_TITLE}
              value={bottomPathKey}
              options={bottomDropdownOptions}
              onChange={option => setBottomPathKey(option)}
            />
          </>
        }
        rightActions={<DownloadDropdown onClickOptions={[excelDownload, pngDownload]} />}
        footerContent={["KPI doesn't apply", 'Source = Platform']}
      >
        {R.isEmpty(processedData) ? (
          <div
            style={{
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '26px'
            }}
          >
            No data to show
          </div>
        ) : (
          <div id='delivery-snapshot-graph-contents' className='delivery-snapshot-contents'>
            <Totals
              topTotal={topTotal}
              bottomTotal={bottomTotal}
              topPathKey={topPathKey}
              bottomPathKey={bottomPathKey}
              metricFormatter={metricFormatter}
            />
            <PacingSnapshotChart
              data={processedData}
              doubleBarChart={isDoubleBar}
              valueFormatter={metricFormatter(Metric.SPEND)}
            />
          </div>
        )}
      </ChartContainer>
    </div>
  );
};

interface TotalsProps {
  topTotal: number;
  bottomTotal: number;
  topPathKey: TopDropdownValuesType;
  bottomPathKey: BottomDropdownValuesType;
  metricFormatter: MetricFormatter;
}

const Totals = ({
  topTotal,
  bottomTotal,
  topPathKey,
  bottomPathKey,
  metricFormatter
}: TotalsProps) => {
  const numericalDifference =
    // If Top is Budget and Bottom is Spend, we want to base the increase based on the bottom because
    // we want to compare Comparison Spend on bottom to Comparison Budget on top
    topPathKey.toLowerCase().includes('budget') && bottomPathKey.toLowerCase().includes('spend')
      ? bottomTotal - topTotal
      : topTotal - bottomTotal || 0;
  const percentDifference = (numericalDifference / bottomTotal) * 100 || 0;
  let differenceLabel: string;

  // If both keys have spend in name or both keys have budget, Total: Increase/Decrease $X (Y%)
  // Otherwise, Total: Over/Under budget $X (Y%)
  if (
    (topPathKey.toLowerCase().includes('spend') && bottomPathKey.toLowerCase().includes('spend')) ||
    (topPathKey.toLowerCase().includes('budget') && bottomPathKey.toLowerCase().includes('budget'))
  ) {
    differenceLabel = numericalDifference >= 0 ? 'Increase' : 'Decrease';
  } else {
    differenceLabel = numericalDifference >= 0 ? 'Over budget' : 'Under budget';
  }

  return (
    <div className='totals-container'>
      <div className='totals-label'>Total: {differenceLabel}</div>
      <div className='totals-diff'>
        {metricFormatter(Metric.SPEND)(Math.abs(numericalDifference))}
      </div>
      <div className='totals-percent-diff'>
        ({`${Math.round(Math.abs(percentDifference)).toLocaleString()}%`})
      </div>
    </div>
  );
};

const processExport = (
  data: CrossChannelMetrics[],
  otherData: CrossChannelMetrics[],
  budgets: BudgetsData[],
  dates: DateRange,
  otherDates: DateRange
) => {
  const channelTotals = new Map();

  const initValues = {
    [`Spend ${dates.start}–${dates.end}`]: 0,
    [`Budget ${dates.start}–${dates.end}`]: 0,
    [`Spend ${otherDates.start}–${otherDates.end}`]: 0,
    [`Budget ${otherDates.start}–${otherDates.end}`]: 0
  };

  data.forEach(item => {
    const { channel, metrics } = item;
    const { spend } = metrics;
    const key = `Spend ${dates.start}–${dates.end}`;

    if (!channelTotals.has(channel)) {
      channelTotals.set(channel, initValues);
    }

    channelTotals.set(channel, {
      ...channelTotals.get(channel),
      [key]: spend
    });
  });

  otherData.forEach(item => {
    const { channel, metrics } = item;
    const { spend } = metrics;
    const key = `Spend ${otherDates.start}–${otherDates.end}`;

    if (!channelTotals.has(channel)) {
      channelTotals.set(channel, initValues);
    }

    channelTotals.set(channel, {
      ...channelTotals.get(channel),
      [key]: spend
    });
  });

  budgets.forEach(item => {
    const { channel, budget, dateRange } = item;

    if (!channelTotals.has(channel)) {
      channelTotals.set(channel, initValues);
    }

    if (dateRange === 'primary') {
      const key = `Budget ${dates.start}–${dates.end}`;
      channelTotals.set(channel, {
        ...channelTotals.get(channel),
        [key]: budget
      });
    } else {
      const key = `Budget ${otherDates.start}–${otherDates.end}`;
      channelTotals.set(channel, {
        ...channelTotals.get(channel),
        [key]: budget
      });
    }
  });

  return Array.from(channelTotals).map(([channel, values]) => ({
    Channel: channel,
    ...values
  }));
};
